import { defaultViewPortPadding } from '@app/services/export/ppt/variables/constants';
import { gridColWidth } from '@app/services/export/ppt/utils/gridUtils';
import PowerPointPresentation from '@app/services/export/ppt/PowerPointPresentation';
import IOfferDSlideData from '@app/types/data/slides/IOfferDSlideData';
import IPriceTableRow from '@app/types/data/base/IPriceTableRow';
import global from '@app/filters/global';
import { PriceTable } from '@app/services/export/ppt/classes/PriceTable';
import { NumberNavigation, INumberNavigationItem } from '@app/services/export/ppt/classes/NumberNavigation';
import AbstractOfferSlideProcessor, {
	IPptOfferSlideOptions
} from '@app/services/export/ppt/slides/AbstractOfferSlideProcessor';
import { SeasonCalendar } from '@app/services/export/ppt/classes/SeasonCalendar';

export default class PowerPointOfferDSlideProcessor extends AbstractOfferSlideProcessor<IOfferDSlideData> {
	public constructor(slideData: IOfferDSlideData, presentation: PowerPointPresentation) {
		const options: Partial<IPptOfferSlideOptions> = {
			imageWidth: defaultViewPortPadding.horizontal + gridColWidth(6)
		};

		super(slideData, presentation, options);
	}

	public async processSlideData(): Promise<void> {
		if (this.slideData.image) {
			await this.adjustImageDimensions(this.slideData.image);
		}

		this.createSlides();
	}

	private createSlides(): void {
		this.slideData.priceMatrix.forEach((item, index) => {
			const { x, y, w } = this.getAvailableSpaceRectangle();
			const offerSlideBase = this.createOfferSlideBase();

			const activityNavigation = new NumberNavigation(
				offerSlideBase,
				{
					links: this.getActivityNavigationData(),
					currentIndex: index,
					singular: this.i18n.activity,
					plural: this.i18n.activities
				},
				{ x, y, w }
			);

			const priceTable = new PriceTable(
				offerSlideBase,
				{
					rows: this.getTableRowsForIndex(index),
					rowLabelWidth: 1.15,
					rowHeight: 0.47
				},
				{ x, y: activityNavigation.getDimensionRect().bottom + 0.2, w }
			);

			const {seasons} = this.slideData;
			if (seasons && seasons.length > 1) {
				const seasonCalendar = new SeasonCalendar(
					offerSlideBase,
					{ seasons, startDateRange: '05-01', endDateRange: '10-31' },
					{ x, w }
				);
				seasonCalendar.create();
			}

			priceTable.create();
			activityNavigation.create();
		});
	}

	private getActivityNavigationData(): INumberNavigationItem[] {
		return this.slideData.priceMatrix.map((item, index) => ({
			numberLabel: item.activityCount,
			slideNumberLink: this.presentation.getSlideNumberById(this.slideData.id) + index
		}));
	}

	private getTableRowsForIndex(index: number): IPriceTableRow[] {
		const i18n: Record<string, string> = this.i18n as Record<string, string>;

		return [
			{
				label: this.i18n.individuals,
				individuals: this.slideData.priceMatrix[index].individuals
			},
			{
				label: this.i18n.groups,
				subline: global.formatI18n(i18n.xPersonsPlus, {
					persons: i18n.minGroupSize
				}),
				groups: this.slideData.priceMatrix[index].groups
			}
		];
	}

	protected getSlideCount(): number {
		return this.slideData.priceMatrix.length;
	}
}
